import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { batch, useComputed, useSignal } from "@preact/signals";
import head from "@tinkoff/utils/array/head";
import FilterPill from "../FilterPill";
import asArray from "../../../lib/as-array";
import OptionList from "../OptionList";
import TextInput from "../../Form/TextInput";
import Divider from "../Divider";
import { isOptionGroup, isOptionGroupArray } from "../../../lib/filter/filter-collection";
import style from "./style.module.scss";
const CUSTOM_VALUE_PREFIX = "Specify: ";
export default function ListFilter(props) {
    const { name, options, disabled, removable, open, fullWidth = false, multiple = false, allowCustomValues = false, closeOnSelect = false, onChange, onRemove, } = props;
    const allOptions = flattenOptions(options);
    const allOptionValues = allOptions.map((option) => option.value);
    const isOpen = useSignal(open);
    const searchTerm = useSignal("");
    let searchable = false;
    if (props.searchable || props.allowCustomValues || allOptions.length > 10) {
        searchable = true;
    }
    const defaultValue = multiple ? asArray(props.defaultValue) : head(asArray(props.defaultValue));
    const selected = useSignal(defaultValue);
    // When custom values are allowed, they are added to the end of the options list
    const customValues = useComputed(() => {
        if (!allowCustomValues) {
            return [];
        }
        const selectedValues = asArray(selected.value).map((option) => option.value);
        let searchTermOption = [];
        if (searchTerm.value && !selectedValues.includes(searchTerm.value)) {
            searchTermOption = [
                {
                    label: `${CUSTOM_VALUE_PREFIX}${searchTerm.value}`,
                    value: searchTerm.value,
                },
            ];
        }
        return asArray(selected.value)
            .concat(searchTermOption)
            .filter((option) => !allOptionValues.includes(option.value));
    });
    const onFilterPillOpenChange = (open) => {
        // We only need to update the `isOpen` signal when `closeOnSelect` is being used.
        if (closeOnSelect) {
            isOpen.value = open;
        }
    };
    const onOptionChange = (value) => {
        // We display a prefix on custom values to indicate to the user that they can select the
        // value that they've typed in the search bar. Here we remove that prefix before selecting
        // the custom value.
        const newValue = asArray(value).map((option) => {
            if (option.label.substring(0, CUSTOM_VALUE_PREFIX.length) === CUSTOM_VALUE_PREFIX) {
                return Object.assign(Object.assign({}, option), { label: option.label.substring(CUSTOM_VALUE_PREFIX.length) });
            }
            return option;
        });
        batch(() => {
            selected.value = newValue;
            if (closeOnSelect) {
                isOpen.value = false;
            }
        });
        if (onChange) {
            onChange(multiple ? newValue : newValue[0]);
        }
    };
    const getOptions = (optionsOrGroup) => {
        if (isOptionGroupArray(optionsOrGroup)) {
            return optionsOrGroup
                .map((group) => (Object.assign(Object.assign({}, group), { options: filterOptions(group.options, searchTerm.value) })))
                .filter((group) => group.options.length)
                .concat(customValues.value.length ? [{ title: "", options: customValues.value }] : [])
                .map((group, index) => (_jsxs(_Fragment, { children: [index > 0 && _jsx(Divider, {}), group.title ? _jsx("div", { className: style["list-filter__title"], children: group.title }) : null, _jsx(OptionList, { options: group.options, selected: selected.value, multiple: multiple, onChange: onOptionChange })] })));
        }
        return (_jsx(OptionList, { options: filterOptions(optionsOrGroup.concat(customValues.value), searchTerm.value), selected: selected.value, multiple: multiple, onChange: onOptionChange }));
    };
    // When `closeOnSelect` is true, we want to have more control over the open state of the
    // FilterPill and use the `isOpen` signal to manage it. Otherwise we just pass through the
    // `open` prop to prevent triggering unnecessary re-renders in FilterPill.
    const filterPillOpenProp = closeOnSelect ? isOpen.value : open;
    return (_jsxs(FilterPill, { attributes: props.attributes, fullWidth: fullWidth, name: name, options: asArray(selected.value), open: filterPillOpenProp, onOpenChange: onFilterPillOpenChange, disabled: disabled, removable: removable, onRemove: onRemove, children: [searchable ? (_jsx("div", { className: style["list-filter__search"], children: _jsx(TextInput, { placeholder: allowCustomValues ? "Search or enter a custom value" : "Search", onInput: (event) => (searchTerm.value = event.currentTarget.value), clearable: true, defaultValue: searchTerm.value }) })) : null, _jsx("div", { tabIndex: -1, className: style["list-filter__options"], children: getOptions(options) })] }));
}
/**
 * Filter an array of options based on a search term
 */
function filterOptions(options, searchTerm) {
    return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
}
/**
 * Collapse an array of filter options, grouped or not, into a flat Array<FilterOption>
 */
function flattenOptions(options) {
    return options.flatMap((option) => {
        if (isOptionGroup(option)) {
            return option.options;
        }
        return option;
    }, []);
}
